'use client';

import { useState } from 'react';
import {
  IconSearch,
  IconClock,
  IconAlertCircle,
  IconCircleCheck,
  IconX
} from '@tabler/icons-react';
import {
  Card,
  Text,
  Title,
  TextInput,
  Select,
  Badge,
  Grid,
  Group,
  Stack,
  Divider,
  Center,
  ThemeIcon
} from '@mantine/core';

export function AdminDashboard({ tickets, onSelectTicket }) {
  const [searchTerm, setSearchTerm] = useState('');
  console.log(tickets);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const filteredTickets = tickets?.filter((ticket) => {
    const matchesSearch =
      ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' || ticket.status === statusFilter;
    const matchesPriority =
      priorityFilter === 'all' || ticket.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <IconAlertCircle size={16} color="red" />;
      case 'in-progress':
        return <IconClock size={16} color="orange" />;
      case 'resolved':
        return <IconCircleCheck size={16} color="green" />;
      case 'closed':
        return <IconX size={16} color="gray" />;
      default:
        return null;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'red';
      case 'in-progress':
        return 'blue';
      case 'resolved':
        return 'green';
      case 'closed':
        return 'gray';
      default:
        return 'blue';
    }
  };

  const ticketsByStatus = {
    open: tickets.filter((t) => t.status === 'open').length,
    'in-progress': tickets.filter((t) => t.status === 'in-progress').length,
    resolved: tickets.filter((t) => t.status === 'resolved').length,
    closed: tickets.filter((t) => t.status === 'closed').length
  };

  return (
    <Stack spacing="md">
      {/* Stats Cards */}
      <Grid>
        <Grid.Col md={3} sm={6} xs={12}>
          <Card withBorder>
            <Group position="apart">
              <div>
                <Text size="sm" color="dimmed">
                  Open
                </Text>
                <Title order={2} color="red">
                  {ticketsByStatus.open}
                </Title>
              </div>
              <ThemeIcon variant="light" color="red" size="lg">
                <IconAlertCircle size={24} />
              </ThemeIcon>
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col md={3} sm={6} xs={12}>
          <Card withBorder>
            <Group position="apart">
              <div>
                <Text size="sm" color="dimmed">
                  In Progress
                </Text>
                <Title order={2} color="orange">
                  {ticketsByStatus['in-progress']}
                </Title>
              </div>
              <ThemeIcon variant="light" color="orange" size="lg">
                <IconClock size={24} />
              </ThemeIcon>
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col md={3} sm={6} xs={12}>
          <Card withBorder>
            <Group position="apart">
              <div>
                <Text size="sm" color="dimmed">
                  Resolved
                </Text>
                <Title order={2} color="green">
                  {ticketsByStatus.resolved}
                </Title>
              </div>
              <ThemeIcon variant="light" color="green" size="lg">
                <IconCircleCheck size={24} />
              </ThemeIcon>
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col md={3} sm={6} xs={12}>
          <Card withBorder>
            <Group position="apart">
              <div>
                <Text size="sm" color="dimmed">
                  Closed
                </Text>
                <Title order={2} color="gray">
                  {ticketsByStatus.closed}
                </Title>
              </div>
              <ThemeIcon variant="light" color="gray" size="lg">
                <IconX size={24} />
              </ThemeIcon>
            </Group>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Filters */}
      <Card withBorder>
        <Stack>
          <div>
            <Title order={3}>Support Tickets</Title>
            <Text color="dimmed">
              Manage and respond to customer support requests
            </Text>
          </div>

          <Group grow>
            <TextInput
              placeholder="Search tickets by subject or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              icon={<IconSearch size={16} />}
            />
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              data={[
                { value: 'all', label: 'All Status' },
                { value: 'open', label: 'Open' },
                { value: 'in-progress', label: 'In Progress' },
                { value: 'resolved', label: 'Resolved' },
                { value: 'closed', label: 'Closed' }
              ]}
              placeholder="Filter by status"
              withinPortal
            />
          </Group>

          <Divider />

          {/* Tickets List */}
          <Stack spacing="sm">
            {filteredTickets.map((ticket) => (
              <Card
                key={ticket.id}
                withBorder
                onClick={() => onSelectTicket(ticket)}
                style={{ cursor: 'pointer' }}
              >
                <Group position="apart" noWrap align="flex-start">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Group spacing="sm" align="center" mb="xs">
                      {getStatusIcon(ticket.status)}
                      <Title order={4}>{ticket.subject}</Title>
                      <Badge color={getPriorityColor(ticket.priority)}>
                        {ticket?.priority?.toUpperCase()}
                      </Badge>
                    </Group>

                    <Text size="sm" color="dimmed" mb="xs" lineClamp={1}>
                      {ticket.email}
                    </Text>

                    <Text size="sm" lineClamp={2} mb="xs">
                      {ticket.description}
                    </Text>

                    <Group spacing="lg" wrap="wrap">
                      <Text size="xs" color="dimmed">
                        ID: {ticket.id}
                      </Text>
                      <Text size="xs" color="dimmed">
                        Created:{' '}
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </Text>
                      <Text
                        size="xs"
                        color="dimmed"
                        style={{ textTransform: 'capitalize' }}
                      >
                        Category: {ticket.issueType}
                      </Text>
                    </Group>
                  </div>

                  {/* Right column → Status Badge */}
                  <Badge
                    color={getStatusColor(ticket.status)}
                    variant="light"
                    ml="md"
                    sx={{ flexShrink: 0 }}
                  >
                    {ticket.status.replace('-', ' ').toUpperCase()}
                  </Badge>
                </Group>
              </Card>
            ))}
          </Stack>

          {filteredTickets?.length === 0 && (
            <Center py="xl">
              <Stack align="center">
                <ThemeIcon variant="light" color="gray" size="xl" radius="xl">
                  <IconAlertCircle size={32} />
                </ThemeIcon>
                <Text color="dimmed">
                  No tickets found matching your criteria.
                </Text>
              </Stack>
            </Center>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
